import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { demoWorkflowSteps, demoSampleData, faqData } from '../data/mockData';
import SafeIcon from '../components/common/SafeIcon';
import { cn } from '../lib/utils';
import * as FiIcons from 'react-icons/fi';
import { 
  FiCheckCircle, FiAlertTriangle, FiLoader, FiInbox, 
  FiFilter, FiChevronRight, FiDatabase, FiActivity, 
  FiInfo, FiHelpCircle, FiChevronDown, FiPlus 
} from 'react-icons/fi';

const FAQItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-navy-700">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left group"
      >
        <span className={cn("text-sm transition-colors", isOpen ? "text-cyan-neon" : "text-slate-300 group-hover:text-white")}>{q}</span>
        <SafeIcon icon={FiChevronDown} className={cn("text-slate-500 transition-transform duration-300", isOpen && "rotate-180 text-cyan-neon")} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-xs text-slate-400 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function DemoView() {
  const [activeStep, setActiveStep] = useState(1);
  const [dataFilter, setDataFilter] = useState('All');
  const [simState, setSimState] = useState('success');

  const filteredData = dataFilter === 'All' 
    ? demoSampleData 
    : demoSampleData.filter(item => item.status === dataFilter);

  return (
    <div className="flex-1 h-full overflow-y-auto bg-navy-900 p-4 md:p-8 lg:p-12 scrollbar-hide">
      <div className="max-w-5xl mx-auto space-y-16 pb-24">
        
        {/* 1. Overview Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-navy-800 rounded-lg border border-navy-700">
              <SafeIcon icon={FiInfo} className="text-cyan-neon" />
            </div>
            <h2 className="text-xl font-semibold text-white uppercase tracking-widest">Overview / What it does</h2>
          </div>
          <div className="glass-panel p-8 rounded-2xl border-l-4 border-l-cyan-neon">
            <p className="text-xl text-white font-medium mb-4">SupportNova is a high-velocity B2B customer support cockpit.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-slate-400">
              <div className="space-y-4">
                <p>
                  <strong className="text-slate-200">The Problem:</strong> Support teams are often drowned in fragmented conversations across multiple tabs, causing high MTTR (Mean Time to Resolution) and agent burnout.
                </p>
                <p>
                  <strong className="text-slate-200">The Solution:</strong> We consolidate the entire support lifecycle—from inbound signal to resolution—into a single, unified "Cockpit" interface.
                </p>
              </div>
              <div className="space-y-4">
                <p>
                  <strong className="text-slate-200">The Outcome:</strong> By automating sentiment analysis and SLA prioritization, teams achieve a <span className="text-cyan-neon">40% reduction in response times</span> and superior CSAT scores.
                </p>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-navy-700 rounded text-[10px] text-cyan-neon font-mono">FOR SAAS TEAMS</span>
                  <span className="px-2 py-1 bg-navy-700 rounded text-[10px] text-cyan-neon font-mono">AI-POWERED</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Demo / How It Works Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-navy-800 rounded-lg border border-navy-700">
              <SafeIcon icon={FiChevronRight} className="text-cyan-neon" />
            </div>
            <h2 className="text-xl font-semibold text-white uppercase tracking-widest">Demo / How It Works</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 space-y-2">
              <h3 className="text-xs font-mono text-slate-500 mb-4 tracking-tighter">TRY THIS FLOW</h3>
              {demoWorkflowSteps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={cn(
                    "w-full text-left p-4 rounded-xl border transition-all duration-300",
                    activeStep === step.id 
                      ? "bg-navy-700 border-cyan-neon shadow-[0_0_15px_rgba(0,240,255,0.1)]" 
                      : "bg-navy-800/50 border-navy-700 hover:border-navy-600"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded flex items-center justify-center text-lg",
                      activeStep === step.id ? "bg-cyan-neon text-navy-900" : "bg-navy-900 text-slate-500"
                    )}>
                      <SafeIcon icon={FiIcons[step.icon]} />
                    </div>
                    <span className={cn("text-sm font-medium", activeStep === step.id ? "text-white" : "text-slate-400")}>
                      {step.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-8 glass-panel rounded-2xl p-8 flex flex-col justify-center min-h-[300px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <h4 className="text-cyan-neon font-mono text-[10px] uppercase tracking-widest mb-2">Step 0{activeStep} Details</h4>
                  <h3 className="text-2xl font-bold text-white mb-4">{demoWorkflowSteps[activeStep - 1].title}</h3>
                  <p className="text-slate-400 text-base leading-relaxed">
                    {demoWorkflowSteps[activeStep - 1].description}
                  </p>
                  <div className="mt-8 pt-6 border-t border-navy-700 flex gap-4">
                    <div className="flex -space-x-2">
                      {[1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-navy-800 bg-navy-600" />)}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono flex flex-col justify-center">
                      <span>AUTOMATED LOG ENTRY</span>
                      <span className="text-cyan-neon/70">PROCESSOR_ID: NV-990</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* 3. Sample Data Section */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-navy-800 rounded-lg border border-navy-700">
                <SafeIcon icon={FiDatabase} className="text-cyan-neon" />
              </div>
              <h2 className="text-xl font-semibold text-white uppercase tracking-widest">Sample Data</h2>
            </div>
            <div className="flex gap-2 bg-navy-800 p-1 rounded-lg border border-navy-700">
              {['All', 'Active', 'Resolved'].map(f => (
                <button key={f} onClick={() => setDataFilter(f)} className={cn("px-3 py-1 text-[10px] rounded uppercase font-bold transition-all", dataFilter === f ? "bg-cyan-neon text-navy-900" : "text-slate-500")}>
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-2xl overflow-hidden border border-navy-700">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-navy-900/50 border-b border-navy-700">
                    <th className="px-6 py-4 text-[10px] font-mono text-slate-500 uppercase">ID</th>
                    <th className="px-6 py-4 text-[10px] font-mono text-slate-500 uppercase">Subject</th>
                    <th className="px-6 py-4 text-[10px] font-mono text-slate-500 uppercase">Impact</th>
                    <th className="px-6 py-4 text-[10px] font-mono text-slate-500 uppercase">Internal Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700">
                  {filteredData.map(item => (
                    <tr key={item.id} className="hover:bg-navy-800/40 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs text-cyan-neon/60">{item.id}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-200">{item.name}</div>
                        <div className="text-[10px] text-slate-500">{item.date} • {item.agent}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={cn(
                          "px-2 py-0.5 rounded text-[9px] font-bold uppercase",
                          item.impact === 'Urgent' ? "bg-red-500/20 text-red-400" : "bg-navy-700 text-slate-400"
                        )}>
                          {item.impact}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[11px] italic text-slate-500">{item.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* 4. Simulated States Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-navy-800 rounded-lg border border-navy-700">
              <SafeIcon icon={FiActivity} className="text-cyan-neon" />
            </div>
            <h2 className="text-xl font-semibold text-white uppercase tracking-widest">Simulated States</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {['before', 'progress', 'success', 'empty', 'warning'].map(state => (
              <button 
                key={state}
                onClick={() => setSimState(state)}
                className={cn(
                  "py-3 rounded-lg border text-[10px] font-bold uppercase transition-all",
                  simState === state ? "bg-cyan-neon text-navy-900 border-cyan-neon" : "bg-navy-800 border-navy-700 text-slate-500"
                )}
              >
                {state}
              </button>
            ))}
          </div>

          <div className="glass-panel p-12 rounded-2xl flex items-center justify-center min-h-[200px]">
             {simState === 'before' && <div className="text-slate-500 text-sm flex items-center gap-2"><SafeIcon icon={FiInbox} /> Awaiting system trigger...</div>}
             {simState === 'progress' && <div className="flex flex-col items-center gap-3"><SafeIcon icon={FiLoader} className="animate-spin text-cyan-neon text-3xl" /><span className="text-xs font-mono text-cyan-neon">CALCULATING SENTIMENT...</span></div>}
             {simState === 'success' && <div className="text-green-400 text-sm flex flex-col items-center gap-2"><SafeIcon icon={FiCheckCircle} className="text-3xl" /><span>SLA COMPLIANCE VERIFIED</span></div>}
             {simState === 'empty' && <div className="text-slate-600 italic text-sm">Workspace is currently clear.</div>}
             {simState === 'warning' && <div className="text-priority-red flex items-center gap-3 bg-red-500/10 p-4 rounded-xl border border-red-500/20"><SafeIcon icon={FiAlertTriangle} /><span>SESSION TIMEOUT IMMINENT</span></div>}
          </div>
        </section>

        {/* 5. FAQ Section */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-navy-800 rounded-lg border border-navy-700">
              <SafeIcon icon={FiHelpCircle} className="text-cyan-neon" />
            </div>
            <h2 className="text-xl font-semibold text-white uppercase tracking-widest">FAQ</h2>
          </div>
          <div className="max-w-3xl">
            {faqData.map((item, i) => <FAQItem key={i} {...item} />)}
          </div>
        </section>

      </div>

      <footer className="text-[10px] text-slate-600 font-mono text-center pb-12 opacity-40 tracking-widest uppercase">
        Made by LingLink AI and DigLab AI
      </footer>
    </div>
  );
}